import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import BeforeAfterSlider from './BeforeAfterSlider';
import FocusTrap from '../../utils/focusTrap';
import './BeforeAfterModal.css';

const BeforeAfterModal = ({ isOpen, onClose, selectedPair }) => {
  const [isVisible, setIsVisible] = useState(false);
  const modalContentRef = useRef(null);
  const focusTrapRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isDraggingSliderRef = useRef(false);

  // Handle arrow key presses to control slider with custom percentage
  const handleArrowKeyPress = useCallback((e) => {
    if (!isOpen || !selectedPair) return;
    
    console.log('Arrow key pressed in BeforeAfterModal:', e.key); // Debug log
    
    // Try multiple selectors to find the slider handle
    const sliderHandle = document.querySelector('.before-after-modal .slider-handle') || 
                        document.querySelector('.slider-handle') ||
                        document.querySelector('[role="slider"]');
    
    if (!sliderHandle) {
      console.log('Slider handle not found'); // Debug log
      return;
    }
    
    // Get current slider position
    const currentPosition = parseFloat(sliderHandle.style.left) || 50;
    const stepPercentage = 5; // You can adjust this percentage (5% per keypress)
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        // If already at 0%, don't do anything - prevent all processing
        if (currentPosition <= 0) {
          return;
        }
        sliderHandle.focus();
        const newLeftPosition = Math.max(0, currentPosition - stepPercentage);
        // Only update if the position actually changed
        if (newLeftPosition !== currentPosition) {
          sliderHandle.style.left = `${newLeftPosition}%`;
          // Update the before image container clip-path only if position changed
          const beforeContainer = document.querySelector('.before-after-modal .before-image-container') ||
                                 document.querySelector('.before-image-container');
          if (beforeContainer) {
            const newClipPath = `inset(0 ${100 - newLeftPosition}% 0 0)`;
            // Only update clip-path if it's different from current
            if (beforeContainer.style.clipPath !== newClipPath) {
              beforeContainer.style.clipPath = newClipPath;
            }
          }
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        // If already at 100%, don't do anything - prevent all processing
        if (currentPosition >= 100) {
          return;
        }
        sliderHandle.focus();
        const newRightPosition = Math.min(100, currentPosition + stepPercentage);
        // Only update if the position actually changed
        if (newRightPosition !== currentPosition) {
          sliderHandle.style.left = `${newRightPosition}%`;
          // Update the before image container clip-path only if position changed
          const beforeContainerRight = document.querySelector('.before-after-modal .before-image-container') ||
                                      document.querySelector('.before-image-container');
          if (beforeContainerRight) {
            const newClipPath = `inset(0 ${100 - newRightPosition}% 0 0)`;
            // Only update clip-path if it's different from current
            if (beforeContainerRight.style.clipPath !== newClipPath) {
              beforeContainerRight.style.clipPath = newClipPath;
            }
          }
        }
        break;
      case 'Home':
        e.preventDefault();
        // If already at 0%, don't do anything
        if (currentPosition <= 0) {
          return;
        }
        sliderHandle.focus();
        sliderHandle.style.left = '0%';
        const beforeContainerHome = document.querySelector('.before-after-modal .before-image-container') ||
                                   document.querySelector('.before-image-container');
        if (beforeContainerHome) {
          const newClipPath = 'inset(0 100% 0 0)';
          // Only update clip-path if it's different from current
          if (beforeContainerHome.style.clipPath !== newClipPath) {
            beforeContainerHome.style.clipPath = newClipPath;
          }
        }
        break;
      case 'End':
        e.preventDefault();
        // If already at 100%, don't do anything
        if (currentPosition >= 100) {
          return;
        }
        sliderHandle.focus();
        sliderHandle.style.left = '100%';
        const beforeContainerEnd = document.querySelector('.before-after-modal .before-image-container') ||
                                  document.querySelector('.before-image-container');
        if (beforeContainerEnd) {
          const newClipPath = 'inset(0 0% 0 0)';
          // Only update clip-path if it's different from current
          if (beforeContainerEnd.style.clipPath !== newClipPath) {
            beforeContainerEnd.style.clipPath = newClipPath;
          }
        }
        break;
      default:
        break;
    }
  }, [isOpen, selectedPair]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;
    
    console.log('Key pressed in BeforeAfterModal:', e.key); // Debug log
    
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        onClose(e);
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
        handleArrowKeyPress(e);
        break;
      default:
        break;
    }
  };

  // Track slider dragging to prevent modal close
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e) => {
      // Check if mousedown is on slider handle or within slider container
      const target = e.target;
      if (target.closest('.slider-handle') || target.closest('.before-after-container')) {
        isDraggingSliderRef.current = true;
      }
    };

    const handleMouseUp = () => {
      // Reset drag state after a short delay to allow click event to process
      setTimeout(() => {
        isDraggingSliderRef.current = false;
      }, 50);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchstart', handleMouseDown);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchstart', handleMouseDown);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isOpen]);

  // Prevent pull-to-refresh on mobile when modal is open
  useEffect(() => {
    if (isOpen) {
      const preventPullToRefresh = (e) => {
        // Prevent pull-to-refresh on the modal overlay
        if (e.target.closest('.before-after-modal-overlay')) {
          e.preventDefault();
        }
      };

      // Add touch event listener with passive: false to allow preventDefault
      document.addEventListener('touchmove', preventPullToRefresh, { passive: false });

      return () => {
        document.removeEventListener('touchmove', preventPullToRefresh);
      };
    }
  }, [isOpen]);

  // Modal effects
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.addEventListener('keydown', handleKeyDown);
      
      // Initialize focus trap
      if (modalContentRef.current) {
        focusTrapRef.current = new FocusTrap(modalContentRef.current);
        focusTrapRef.current.activate();
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        
        // Deactivate focus trap
        if (focusTrapRef.current) {
          focusTrapRef.current.deactivate();
          focusTrapRef.current = null;
        }
        
        setIsVisible(false);
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div 
      className="before-after-modal-overlay" 
      onClick={(e) => {
        // Don't close if we were just dragging the slider
        if (isDraggingSliderRef.current) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onClose(e);
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="before-after-modal-title"
    >
      <div 
        ref={modalContentRef}
        className="before-after-modal-content" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          type="button"
          className="before-after-modal-close-btn"
          onClick={(e) => onClose(e)}
          aria-label="Close before/after modal"
        >
          <FaTimes />
        </button>

        {/* Modal Header */}
        <div className="before-after-modal-header">
          <div className="before-after-modal-header-content">
            <h2 id="before-after-modal-title" className="before-after-modal-title">
              <span className="title-before">Before</span>
              <span className="title-separator">&</span>
              <span className="title-after">After</span>
            </h2>
          </div>
        </div>

        {/* Before/After Slider */}
        <div className="before-after-modal-slider-container">
          {selectedPair && (
            <BeforeAfterSlider
              beforeImage={selectedPair.before.src}
              afterImage={selectedPair.after.src}
              alt={`Before and after comparison for project ${selectedPair.projectNumber}`}
            />
          )}
        </div>

        {/* Modal Footer */}
        <div className="before-after-modal-footer">
          <div className="before-after-modal-instructions">
            <span className="desktop-instructions">Drag the slider or use ← and → arrow keys to compare before and after</span>
            <span className="mobile-instructions">Drag the slider to compare before and after</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterModal;
